import { ComponentProps, ReactNode } from 'react';

import { NavButton } from '~/modules/elements/nav-controls';

type ActionProps = Omit<ComponentProps<typeof NavButton>, 'className'>;

// `NavButton` takes the label it mirrors to the phone from its children, and only when they are a
// plain string - which they are not once a wrapper puts them inside a span. Every one of these
// wrappers does, so each passes the string on itself; without it the phone draws the row with no
// text at all, which is what Singers, Manage Songs and Settings looked like.
const mirroredLabel = (remoteLabel: string | undefined, children: ReactNode) =>
  remoteLabel ?? (typeof children === 'string' ? children : undefined);

/**
 * The one thing the screen exists for.
 *
 * Sized and lit rather than recoloured: an accent fill is what `ButtonBase` already means by
 * "focused", and this button has to go on reading as the primary once the focus moves off it.
 */
export function PrimaryAction({ children, remoteLabel, ...props }: ActionProps) {
  return (
    <NavButton
      {...props}
      remoteLabel={mirroredLabel(remoteLabel, children)}
      className="mobile:h-20 h-27 rounded-[0.625rem] shadow-[0_0_36px_rgba(255,165,0,0.28),inset_0_0_0_1px_rgba(255,165,0,0.45)]">
      {/* The label alone, centred. It used to share the row with the catalogue's size, which is a
          number about the screen this button opens rather than about the button. */}
      <span className="mobile:text-lg truncate text-[2.375rem] leading-none">{children}</span>
    </NavButton>
  );
}

/**
 * The other two places worth going. Keeps `ButtonBase`'s own amber hairline (`shadow-focusable`),
 * which is exactly the ring the frame draws around this tier.
 */
export function SecondaryAction({ wide = false, children, remoteLabel, ...props }: ActionProps & { wide?: boolean }) {
  return (
    <NavButton
      {...props}
      remoteLabel={mirroredLabel(remoteLabel, children)}
      className={`mobile:h-16 h-17 rounded-lg text-[1.375rem] ${wide ? 'lg:col-span-2' : ''}`}>
      {children}
    </NavButton>
  );
}

/** Everything a room sets up once and then stops thinking about. Labels left-aligned so they line up. */
export function UtilityAction({ state, children, remoteLabel, ...props }: ActionProps & { state?: ReactNode }) {
  return (
    <NavButton
      {...props}
      remoteLabel={mirroredLabel(remoteLabel, children)}
      remoteNote={typeof state === 'string' ? state : undefined}
      className="mobile:h-14 text-md h-[3.875rem] rounded-lg px-5 text-white/85 shadow-[inset_0_0_0_1px_rgba(255,255,255,0.1)]">
      <span className="flex w-full items-center gap-3">
        <span className="truncate">{children}</span>
        {/* The label gives way before the state does: "none yet" is the whole reason the row says
            anything, and it is the shorter of the two. */}
        {state && <span className="text-active ml-auto shrink-0 text-sm tracking-[0.06em]">{state}</span>}
      </span>
    </NavButton>
  );
}
